import { useCallback, useEffect, useId, useRef, useState } from 'react'

/**
 * Open state for a popover opened by a button: closes on a click outside
 * the container or on Escape. Focus goes back to the button when it was in
 * the popover, e.g. on Escape or after picking an option, so it isn't lost
 * when the panel unmounts.
 */
export default function usePopover<T extends HTMLElement = HTMLDivElement>() {
  const [isOpen, setIsOpen] = useState(false)

  const containerRef = useRef<T>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverId = useId()

  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen(open => !open), [])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const container = containerRef.current
    const trigger = triggerRef.current
    let isFocusInside = !!container?.contains(document.activeElement)

    function handleFocusIn(event: FocusEvent) {
      isFocusInside = !!container?.contains(event.target as Node)
    }

    function handlePointerDown(event: PointerEvent) {
      if (!container?.contains(event.target as Node)) {
        isFocusInside = false
        setIsOpen(false)
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        // Handled here, so a modal around the popover stays open
        event.preventDefault()
        isFocusInside = true
        setIsOpen(false)
      }
    }

    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)

      const { activeElement } = document
      const isFocusLost =
        !activeElement ||
        activeElement === document.body ||
        !!container?.contains(activeElement)

      // The focused option unmounted with the panel
      if (isFocusInside && isFocusLost) {
        trigger?.focus()
      }
    }
  }, [isOpen])

  return {
    isOpen,
    close,
    toggle,
    containerRef,
    triggerRef,
    /** Spread on the trigger button */
    triggerProps: {
      'aria-expanded': isOpen,
      'aria-controls': isOpen ? popoverId : undefined,
      onClick: toggle,
    },
    popoverId,
  }
}
